'use client';

import styled from 'styled-components';

import { breakpoints } from '@styles/breakpoint';
import { GridLayout, GridTemplateAreas } from '@styles/GridVariables';
import { Spacers } from '@styles/theme';

interface ModalContainerProps {
  $width?: string;
  $height?: string;
}

export const ModalContainer = styled.dialog<{ $fullScreen?: boolean }>`
  display: flex;
  flex-direction: column;
  padding: ${Spacers.BASE}rem;
  border-radius: 0.5rem;
  border: 1px solid ${(props) => props.theme.colors.backgroundAccent};
  position: fixed;
  bottom: 0;
  width: 100%;
  height: ${(props) => props.$fullScreen && '100%'};
  gap: ${Spacers.LG}rem;
  border-radius: 0.5rem 0.5rem 0 0;
  background-color: ${({ theme }) => theme.colors.backgroundLight};
  grid-area: content;
  z-index: 100;

  ${breakpoints.desktopOrBigger} {
    position: relative;
    border-radius: 0.5rem;
    grid-area: content;
    height: initial;
    padding: ${Spacers.LG}rem ${Spacers.BASE}rem;
  }
`;

export const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background-color: ${({ theme }) => theme.colors.backgroundDarkOverlay};
  display: grid;
  align-items: center;
  justify-content: center;
  z-index: 100; /* Ensure it appears above other content */

  // The container is fixed that's why we have to reuse grid for bottom layout.
  ${breakpoints.desktopOrBigger} {
    grid-template-columns: ${GridLayout.desktop.columns};
    gap: ${GridLayout.desktop.gap};
    grid-template-areas: ${GridTemplateAreas.desktop};
    padding: 0;
  }
`;

export const Divider = styled.div`
  height: 1px;
  background-color: ${({ theme }) => theme.colors.backgroundAccent};
  width: calc(100% + (${Spacers.BASE}rem * 2));
  margin-left: -${Spacers.BASE}rem;
  margin-top: -${Spacers.XS}rem;

  ${breakpoints.desktopOrBigger} {
    display: none;
  }
`;

export const TitleContainer = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
`;

export const Text = styled.p`
  padding-block: ${Spacers.LG}rem;
  color: ${({ theme }) => theme.colors.typographyBodyLight};
  text-align: left;
`;

export const CloseButton = styled.button`
  border: none;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.typographyHeadersLight};
  &:hover {
    color: ${(props) => props.theme.colors.actionDefault};
  }
`;

export const ButtonsContainer = styled.div`
  display: flex;
  gap: ${Spacers.LG}rem;
  width: 100%;
  justify-content: center;
  }
`;

export const SearchModalContainer = styled.dialog<ModalContainerProps>`
  display: flex;
  flex-direction: column;
  padding: ${Spacers.BASE}rem;
  border-radius: 0.5rem;
  border: 1px solid ${(props) => props.theme.colors.backgroundAccent};
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  width: 100%;
  height: 100%;
  border-radius: 0.5rem 0.5rem 0 0;
  background-color: ${({ theme }) => theme.colors.backgroundLight};
  grid-area: content;
  z-index: 100;
  overflow-y: auto;

  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 0.5rem;
  }

  &::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.colors.backgroundLight};
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.backgroundAccent};
  }

  &::-webkit-scrollbar-thumb:hover {
    background: ${({ theme }) => theme.colors.backgroundLight};
  }

  /* Firefox scrollbar styling */
  scrollbar-color: ${({ theme }) => theme.colors.backgroundAccent}
    ${({ theme }) => theme.colors.backgroundLight};

  ${breakpoints.desktopOrBigger} {
    position: relative;
    border-radius: 0.5rem;
    grid-area: content;
    width: ${({ $width }) => $width || '100%'};
    height: ${({ $height }) => $height || '100%'};
  }
`;

export const SearchContentContainer = styled.div`
  margin-bottom: ${Spacers.LG}rem;
  width: 100%;
`;